import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, MoreHorizontal, Send, Pencil as Edit3, Eye, Copy, Trash2, Clock, CircleCheck as CheckCircle2, Circle as XCircle, FileCheck, LayoutTemplate, Search, Filter, ChevronDown, X, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';
import { Button } from '@/components/ui/button';

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useWorkspace } from '@/context/WorkspaceContext';
import { proposalService, type Proposal, type ProposalStatus, type ProposalTemplate } from '@/lib/proposalService';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface ProposalsProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

const statusConfig: Record<ProposalStatus, { label: string; icon: any; className: string }> = {
  draft: { label: 'Draft', icon: Edit3, className: 'bg-gray-100 text-gray-700 border-gray-200' },
  pending: { label: 'Pending', icon: Clock, className: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Approved', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  declined: { label: 'Declined', icon: XCircle, className: 'bg-rose-50 text-rose-700 border-rose-200' },
  expired: { label: 'Expired', icon: Clock, className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const Proposals: React.FC<ProposalsProps> = ({ isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }) => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ProposalStatus>('all');
  const [tab, setTab] = useState<'proposals' | 'templates'>('proposals');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [editingDateFor, setEditingDateFor] = useState<Proposal | null>(null);
  const [newValidDate, setNewValidDate] = useState<string>('');

  const load = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      setLoading(true);
      const [p, t] = await Promise.all([
        proposalService.listProposals(currentWorkspace.id),
        proposalService.listTemplates(currentWorkspace.id),
      ]);
      setProposals(p);
      setTemplates(t);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => { load(); }, [load]);

  const handleMobileMenuToggle = () => {
    const crmMenuItem = navigationItems.find(item => item.id === 'crm');
    if (crmMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('crm', crmMenuItem.subItems, crmMenuItem.label);
    }
  };

  const handleCreateNew = async () => {
    if (!currentWorkspace) return;
    try {
      const proposal = await proposalService.createProposal({ workspace_id: currentWorkspace.id });
      navigate(`/proposals/${proposal.id}/edit`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create proposal');
    }
  };

  const handleUseTemplate = async (templateId: string) => {
    if (!currentWorkspace) return;
    try {
      const proposal = await proposalService.createFromTemplate(templateId, currentWorkspace.id);
      navigate(`/proposals/${proposal.id}/edit`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to create from template');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this proposal? This cannot be undone.')) return;
    try {
      await proposalService.deleteProposal(id);
      setProposals(prev => prev.filter(p => p.id !== id));
      toast.success('Proposal deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleDuplicate = async (p: Proposal) => {
    if (!currentWorkspace) return;
    try {
      const copy = await proposalService.createProposal({
        workspace_id: currentWorkspace.id,
        title: `${p.title} (Copy)`,
        cover_image_url: p.cover_image_url,
        content: p.content,
        client_email: p.client_email,
        client_name: p.client_name,
        company_name: p.company_name,
      });
      navigate(`/proposals/${copy.id}/edit`);
    } catch {
      toast.error('Failed to duplicate');
    }
  };

  const handleUpdateValidDate = async () => {
    if (!editingDateFor || !newValidDate) return;
    try {
      const isFuture = new Date(newValidDate) > new Date();
      let newStatus = editingDateFor.status;
      if (editingDateFor.status === 'expired' && isFuture) newStatus = 'pending';
      if (editingDateFor.status === 'pending' && !isFuture) newStatus = 'expired';

      const updated = await proposalService.updateProposal(editingDateFor.id, {
        valid_until: new Date(newValidDate).toISOString(),
        status: newStatus
      });
      setProposals(prev => prev.map(p => p.id === updated.id ? updated : p));
      toast.success('Validation date updated');
      setEditingDateFor(null);
    } catch {
      toast.error('Failed to update validation date');
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Delete this template?')) return;
    try {
      await proposalService.deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const filtered = proposals.filter(p => {
    const query = (isMobile ? mobileSearchQuery : search).toLowerCase();
    if (query && !p.title.toLowerCase().includes(query) && !p.client_name.toLowerCase().includes(query)) {
      return false;
    }
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Proposals | AgencyPlus</title>
        <link rel="canonical" href="/proposals" />
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={handleCreateNew}
          searchValue={mobileSearchQuery}
          onSearchChange={setMobileSearchQuery}
          searchPlaceholder="Search proposals..."
          showSearch
          showAdd
          isDarkMode={isGlobalDarkMode}
        />
      )}

      <div className={cn('w-full px-6 md:px-10 py-6', isMobile && 'pt-20')}>
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Proposals</h1>
            <p className="text-sm text-muted-foreground mt-1">Create, send, and track proposals for your clients</p>
          </div>
          {!isMobile && (
            <div className="flex items-center gap-2">
              {tab === 'proposals' && (
                <>
                  {/* Search — collapsible */}
                  <div className="relative flex items-center h-10">
                    <AnimatePresence initial={false}>
                      {searchOpen ? (
                        <motion.div
                          key="search-open"
                          className="relative flex items-center h-10"
                          initial={{ width: 40, opacity: 0.6 }}
                          animate={{ width: 256, opacity: 1 }}
                          exit={{ width: 40, opacity: 0 }}
                          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                        >
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <input
                            ref={searchInputRef}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search proposals..."
                            onBlur={() => { if (!search) setSearchOpen(false); }}
                            className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-workspace-accent/30"
                          />
                          <button
                            onClick={() => { setSearch(''); setSearchOpen(false); }}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </motion.div>
                      ) : (
                        <motion.button
                          key="search-icon"
                          onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                          className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <Search className="w-4 h-4" />
                        </motion.button>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Filter Toggle */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className={`flex items-center gap-2 px-4 h-10 rounded-lg border transition-colors whitespace-nowrap ${
                        statusFilter !== 'all' 
                          ? 'bg-workspace-accent text-white border-transparent' 
                          : 'bg-card border-border text-foreground hover:bg-muted'
                      }`}>
                        <Filter className="w-4 h-4" />
                        <span className="text-sm font-medium">{statusFilter === 'all' ? 'Filter' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}</span>
                        <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover border-border">
                      <DropdownMenuItem onClick={() => setStatusFilter('all')} className="flex items-center justify-between">
                        All Statuses {statusFilter === 'all' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatusFilter('draft')} className="flex items-center justify-between">
                        Draft {statusFilter === 'draft' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatusFilter('pending')} className="flex items-center justify-between">
                        Pending {statusFilter === 'pending' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatusFilter('approved')} className="flex items-center justify-between">
                        Approved {statusFilter === 'approved' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatusFilter('declined')} className="flex items-center justify-between">
                        Declined {statusFilter === 'declined' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setStatusFilter('expired')} className="flex items-center justify-between">
                        Expired {statusFilter === 'expired' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}

              <Button onClick={handleCreateNew} className="gap-2 bg-workspace-accent hover:bg-workspace-accent/90 text-white h-10">
                <Plus className="w-4 h-4" /> New Proposal
              </Button>
            </div>
          )}
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as any)} className="w-full">
          <TabsList className="mb-6 bg-transparent border-b border-border w-full justify-start rounded-none p-0 h-auto space-x-6">
            <TabsTrigger 
              value="proposals" 
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-workspace-accent data-[state=active]:text-foreground text-muted-foreground rounded-none px-0 pb-3 pt-2 text-sm font-normal data-[state=active]:font-normal border-b-2 border-transparent shadow-none data-[state=active]:shadow-none flex items-center gap-2"
            >
              <FileText className="w-4 h-4" /> Proposals
            </TabsTrigger>
            <TabsTrigger 
              value="templates" 
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-workspace-accent data-[state=active]:text-foreground text-muted-foreground rounded-none px-0 pb-3 pt-2 text-sm font-normal data-[state=active]:font-normal border-b-2 border-transparent shadow-none data-[state=active]:shadow-none flex items-center gap-2"
            >
              <LayoutTemplate className="w-4 h-4" /> Templates
            </TabsTrigger>
          </TabsList>

          <TabsContent value="proposals" className="mt-0">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Proposals</span>
                  <FileText className="w-5 h-5 text-workspace-accent opacity-20" />
                </div>
                <div className="text-3xl font-bold text-foreground">{proposals.length}</div>
              </div>
              <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Approved</span>
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 opacity-20" />
                </div>
                <div className="text-3xl font-bold text-foreground">{proposals.filter(p => p.status === 'approved').length}</div>
              </div>
              <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Pending</span>
                  <Clock className="w-5 h-5 text-amber-500 opacity-20" />
                </div>
                <div className="text-3xl font-bold text-foreground">{proposals.filter(p => p.status === 'pending').length}</div>
              </div>
            </div>

            {loading ? (
              <div className="py-20 text-center text-muted-foreground">Loading proposals...</div>
            ) : filtered.length === 0 ? (
              <EmptyState onCreate={handleCreateNew} />
            ) : (
              <div className="rounded-2xl border border-border bg-white dark:bg-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 border-b border-border">
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="px-5 py-3 font-medium">Title</th>
                      <th className="px-5 py-3 font-medium">Client</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium">Valid Until</th>
                      <th className="px-5 py-3 font-medium">Updated</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => {
                      const cfg = statusConfig[p.status];
                      const Icon = cfg.icon;
                      return (
                        <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                          <td className="px-5 py-4">
                            <button
                              onClick={() => navigate(`/proposals/${p.id}/edit`)}
                              className="font-semibold text-foreground hover:text-workspace-accent transition-colors text-left"
                            >
                              {p.title}
                            </button>
                          </td>
                          <td className="px-5 py-4 text-muted-foreground">{p.client_name || p.client_email || '—'}</td>
                          <td className="px-5 py-4">
                            <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium', cfg.className)}>
                              <Icon className="w-3 h-3" /> {cfg.label}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-muted-foreground">
                            {p.valid_until ? format(new Date(p.valid_until), 'MMM d, yyyy') : '—'}
                          </td>
                          <td className="px-5 py-4 text-muted-foreground">{format(new Date(p.updated_at), 'MMM d, yyyy')}</td>
                          <td className="px-5 py-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/proposals/${p.id}/edit`)}>
                                  <Edit3 className="w-4 h-4" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/proposals/${p.id}/preview`)}>
                                  <Eye className="w-4 h-4" /> Preview
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicate(p)}>
                                  <Copy className="w-4 h-4" /> Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setEditingDateFor(p); setNewValidDate(p.valid_until ? p.valid_until.split('T')[0] : ''); }}>
                                  <Calendar className="w-4 h-4" /> Edit Expiry Date
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem destructive onClick={() => handleDelete(p.id)}>
                                  <Trash2 className="w-4 h-4" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="templates" className="mt-0">
            {loading ? (
              <div className="py-20 text-center text-muted-foreground">Loading templates...</div>
            ) : templates.length === 0 ? (
              <div className="py-20 text-center rounded-2xl border border-dashed border-border">
                <LayoutTemplate className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-1">No templates yet</h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                  From any open proposal, click "Save as Workspace Template" to reuse its content.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {templates.map(t => (
                  <div key={t.id} className="rounded-2xl border border-border bg-white dark:bg-card overflow-hidden group hover:shadow-md transition-shadow">
                    <div className="aspect-video bg-muted relative overflow-hidden">
                      {t.cover_image_url ? (
                        <img src={t.cover_image_url} alt={t.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                          <FileCheck className="w-10 h-10 text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold mb-1">{t.name}</h3>
                      <p className="text-xs text-muted-foreground mb-3">Saved {format(new Date(t.created_at), 'MMM d, yyyy')}</p>
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="flex-1 gap-2" onClick={() => handleUseTemplate(t.id)}>
                          <Plus className="w-3.5 h-3.5" /> Use Template
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeleteTemplate(t.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editingDateFor} onOpenChange={(v) => !v && setEditingDateFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Valid Until Date</DialogTitle>
            <DialogDescription>
              Change the expiration date for this proposal. If it has already expired, setting a future date will reactivate it.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="valid-date">Valid Until</Label>
            <Input 
              id="valid-date"
              type="date"
              value={newValidDate}
              onChange={(e) => setNewValidDate(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDateFor(null)}>Cancel</Button>
            <Button onClick={handleUpdateValidDate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EmptyState: React.FC<{ onCreate: () => void }> = ({ onCreate }) => (
  <div className="py-20 flex flex-col items-center justify-center rounded-2xl border border-dashed border-border">
    <div className="w-20 h-20 rounded-full flex items-center justify-center mb-5 bg-workspace-accent/10">
      <Send className="w-9 h-9 text-workspace-accent" />
    </div>
    <h3 className="text-xl font-bold mb-2">Send your first proposal</h3>
    <p className="text-muted-foreground text-center max-w-md mb-6">
      Build a beautiful, branded proposal in minutes. Merge tags, signatures, and PDF export — all included.
    </p>
    <Button onClick={onCreate} className="gap-2 bg-workspace-accent hover:bg-workspace-accent/90 text-white">
      <Plus className="w-4 h-4" /> Create Proposal
    </Button>
  </div>
);

export default Proposals;
